import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import Swal from 'sweetalert2';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PersonIcon from '@mui/icons-material/Person';

import PageHeading from 'allGlobalComponents/PageHeading';
import GlobalTabs from 'allGlobalComponents/GlobalTabs';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';
import TableOmniExpandable from 'allGlobalComponents/TableOmniExpandable';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import DeleteConfirmationDialog from 'allGlobalComponents/DeleteConfirmationDoalog';
import { useApi } from 'app/hooks';
import { globalDateFormat } from 'app/constants';
import {
	CAMPAIGN_APPROVALS_MODULE,
	CAMPAIGN_APPROVALS_METHODS,
	LIST_INITIAL_PAGE,
	LIST_LIMIT,
	ACCOUNT_MANAGERS,
	ACTIONS,
	APPROVAL_STATUS,
	TIMING_MODES,
	SUB_CATEGORIES,
	TEMPLATE_CATEGORY_UTILITY,
	TEMPLATE_COMPONENT_BODY,
	ACTION_OPTIONS,
	TIMING_OPTIONS,
	UTILITY_SUB_CATEGORY_OPTIONS,
	INSTANT_DELAY_MINUTES,
	MINUTE_UNIT,
	DATETIME_LOCAL_FORMAT,
	COLORS,
	CATEGORY_PILLS,
	TYPE_PILLS,
	TABLE_PER_PAGE,
	TABLE_ROWS_PER_PAGE_OPTIONS,
	TABLE_MAX_HEIGHT,
	TRUNCATE,
	REVIEW_MODAL_WIDTH,
	INLINE_SPINNER_SIZE,
	NOTES_ROWS,
	BUTTON_ICON_STYLE,
	CONFIRM_LIST_MAX_HEIGHT,
	SWAL_CLASS,
	SWAL_WIDTH
} from './campaignApprovalsConstants';

const useStyles = makeStyles(theme => ({
	content: {
		width: '100%',
		maxWidth: '100%',
		minWidth: 0,
		boxSizing: 'border-box',
		padding: '0 16px'
	},
	formContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2.5),
		padding: theme.spacing(2)
	},
	summary: {
		fontSize: 14,
		color: theme.palette.text.secondary
	},
	formActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: theme.spacing(2),
		width: '100%'
	},
	'@keyframes fieldShake': {
		'0%, 100%': { transform: 'translateX(0)' },
		'20%, 60%': { transform: 'translateX(-6px)' },
		'40%, 80%': { transform: 'translateX(6px)' }
	},
	shakeField: {
		animation: '$fieldShake 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97) both'
	}
}));

// Two hardcoded account-manager tabs (icon lives here since it's a component import).
const ALL_TABS = ACCOUNT_MANAGERS.map(name => ({ id: name, label: name, icon: PersonIcon }));

// Restrict the visible tabs to the logged-in account manager. A given account only
// sees (and can act on) its own manager tab; anyone else sees all tabs.
const MANAGER_BY_USER_ID = { 729: 'Faheem Khan', 1204: 'Fahad Khan' };
const getScopedTabs = () => {
	const userInfo = JSON.parse(localStorage.getItem('user_data') || '{}');
	const manager = userInfo && userInfo.id && MANAGER_BY_USER_ID[userInfo.id];
	if (manager) {
		const own = ALL_TABS.filter(t => t.id === manager);
		if (own.length) return own;
	}
	return ALL_TABS;
};
const TABS = getScopedTabs();

const isUtilityCategory = category => String(category || '').toUpperCase() === TEMPLATE_CATEGORY_UTILITY;

// Resolve the internal_sub_category to send for one campaign.
const resolveSubCategory = (campaign, utilityChoice) =>
	isUtilityCategory(campaign?.template_category) ? utilityChoice : SUB_CATEGORIES.STANDARD;

// Convert an ISO/UTC time into the local `datetime-local` input format.
const toDateTimeLocal = value => (value ? moment(value).format(DATETIME_LOCAL_FORMAT) : '');

// ---- Pill mappings ----
const neutralPill = val => ({ label: val || '-', color: COLORS.neutralText, bgColor: COLORS.neutralBg });
const categoryPill = val => CATEGORY_PILLS[String(val || '').toLowerCase()] || neutralPill(val);
const typePill = val => TYPE_PILLS[String(val || '').toLowerCase()] || neutralPill(val);
const statusPill = val => {
	const v = String(val || '').toLowerCase();
	if (v === APPROVAL_STATUS.APPROVED)
		return { label: 'Approved', color: COLORS.successText, bgColor: COLORS.successBg };
	if (v === APPROVAL_STATUS.REJECTED)
		return { label: 'Rejected', color: COLORS.dangerText, bgColor: COLORS.dangerBg };
	return { label: 'Pending', color: COLORS.warningText, bgColor: COLORS.warningBg };
};

// Pull the template body text out of a campaign's message_body payload.
const extractTemplateBody = campaign => {
	const components = campaign?.message_body?.template?.components || [];
	const body = components.find(c => c.type === TEMPLATE_COMPONENT_BODY);
	return body?.text || '-';
};

// ---- Swal helpers (prefer the API's message, fall back to a frontend one) ----
const baseSwal = { customClass: SWAL_CLASS, width: SWAL_WIDTH };
// Success body: { data: { message } } or { message }.
const extractSuccessMessage = response => response?.data?.data?.message || response?.data?.message || null;
// Error body (axios): err.response.data = { error, message, data, code } → message.
const extractErrorMessage = err => err?.response?.data?.message || null;

const showApiSuccess = (response, fallback) =>
	Swal.fire({ ...baseSwal, title: 'Success', icon: 'success', text: extractSuccessMessage(response) || fallback });
const showApiError = (err, fallback) =>
	Swal.fire({ ...baseSwal, title: 'Error', icon: 'error', text: extractErrorMessage(err) || fallback });
const showWarning = text => Swal.fire({ ...baseSwal, title: 'Partially Done', icon: 'warning', text });
// Frontend-only validation (no API call involved).
const showValidationError = text => Swal.fire({ ...baseSwal, title: 'Error', icon: 'error', text });

function CampaignApprovals() {
	const classes = useStyles();
	const { isLoading: listLoading, request: listRequest } = useApi();
	const { request: actionRequest } = useApi();

	// Data
	const [clients, setClients] = useState([]);
	const [refreshKey, setRefreshKey] = useState(0);

	// Tabs + selection (selection is a flat array of campaign ids, scoped to the active tab)
	const [activeTab, setActiveTab] = useState(TABS[0].id);
	const [selectedCampaigns, setSelectedCampaigns] = useState([]);

	// Review dialog
	const [reviewOpen, setReviewOpen] = useState(false);
	const [action, setAction] = useState(ACTIONS.APPROVE);
	// Sub-category applied to UTILITY campaigns on approve (non-utility → always STANDARD).
	// Default is the "Utility" choice, which itself maps to STANDARD.
	const [utilitySubCategory, setUtilitySubCategory] = useState(SUB_CATEGORIES.STANDARD);
	// Optional new send time on approve (datetime-local value). Empty = don't send.
	const [sendTime, setSendTime] = useState('');
	// When a past-scheduled campaign is approved: INSTANT (now + delay) or SCHEDULE (pick a time).
	const [timingMode, setTimingMode] = useState(TIMING_MODES.INSTANT);
	const [notes, setNotes] = useState('');
	// Which fields the user has interacted with — errors only show once touched
	// (on blur) or after a submit attempt: { notes, subCategory, time }.
	const [touched, setTouched] = useState({});
	// Transient flags to shake a field (attention nudge) on an invalid submit.
	const [shakeFields, setShakeFields] = useState({});

	// Confirmation dialog
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// ---- Fetch pending campaigns ----
	useEffect(() => {
		listRequest({
			module: CAMPAIGN_APPROVALS_MODULE,
			method: CAMPAIGN_APPROVALS_METHODS.list,
			payload: { status: APPROVAL_STATUS.PENDING, page: LIST_INITIAL_PAGE, limit: LIST_LIMIT },
			onSuccess: response => {
				setClients(response?.data?.data?.data?.clients || []);
			},
			onError: (_message, err) => {
				setClients([]);
				showApiError(err, 'Failed to load campaign approvals.');
			}
		});
		// Reset selection on every reload so stale ids never linger.
		setSelectedCampaigns([]);
	}, [refreshKey, listRequest]);

	const handleRefresh = () => setRefreshKey(prev => prev + 1);

	// ---- Front-end filtering by account manager (per tab) ----
	const filteredClients = useMemo(
		() => clients.filter(c => (c.account_manager || '').trim() === activeTab),
		[clients, activeTab]
	);

	// Flat lookup of every campaign by id (across all clients) — used to resolve
	// each selected campaign's template_category at approve time.
	const campaignsById = useMemo(() => {
		const map = {};
		clients.forEach(c =>
			(c.campaigns || []).forEach(camp => {
				map[String(camp.id)] = camp;
			})
		);
		return map;
	}, [clients]);

	// Does the current selection include at least one UTILITY campaign?
	const selectionHasUtility = useMemo(
		() => selectedCampaigns.some(id => isUtilityCategory(campaignsById[String(id)]?.template_category)),
		[selectedCampaigns, campaignsById]
	);

	const handleTabChange = tabId => {
		setActiveTab(tabId);
		setSelectedCampaigns([]);
	};

	// ---- Table columns ----
	const outerHeadCells = [
		{ id: 'client_name', numeric: false, label: 'Client', tooltip: true, customRow: r => r.client_name || '-' },
		{ id: 'client_id', numeric: false, label: 'Client ID', tooltip: true },
		{
			id: 'account_manager',
			numeric: false,
			label: 'Account Manager',
			customRow: r => r.account_manager || '-'
		},
		{
			id: 'campaigns',
			numeric: true,
			label: 'Pending Campaigns',
			disableSort: true,
			customRow: r => (r.campaigns ? r.campaigns.length : 0)
		}
	];

	const innerHeadCells = [
		{ id: 'id', numeric: false, label: 'Campaign ID', tooltip: true },
		{
			id: 'recipient_id',
			numeric: false,
			label: 'Recipient ID',
			tooltip: true,
			disableSort: true,
			// recipient_id is always a single-element array.
			customRow: r => (Array.isArray(r.recipient_id) ? r.recipient_id[0] : r.recipient_id) || '-'
		},
		{
			id: 'recipients_count',
			numeric: true,
			label: 'Count',
			customRow: r => (r.recipients_count !== undefined && r.recipients_count !== null ? r.recipients_count : '-')
		},
		{
			id: 'template_name',
			numeric: false,
			label: 'Template',
			tooltip: true,
			customRow: r => r.template_name || '-'
		},
		{
			id: 'template_category',
			numeric: false,
			label: 'Category',
			pill: true,
			pillConfig: { mapping: categoryPill }
		},
		{ id: 'campaign_type', numeric: false, label: 'Type', pill: true, pillConfig: { mapping: typePill } },
		{
			id: 'approval_status',
			numeric: false,
			label: 'Status',
			pill: true,
			pillConfig: { mapping: statusPill }
		},
		{
			id: 'message',
			numeric: false,
			label: 'Message',
			msg: true,
			truncateLength: TRUNCATE.MESSAGE,
			disableSort: true,
			customRow: extractTemplateBody
		},
		{ id: 'time', numeric: false, label: 'Scheduled', dt: true },
		{
			id: 'created_by_username',
			numeric: false,
			label: 'Created By',
			customRow: r => r.created_by_username || '-'
		},
		{ id: 'dt', numeric: false, label: 'Created At', dt: true }
	];

	// ---- Review flow ----
	const openReview = () => {
		setAction(ACTIONS.APPROVE);
		setUtilitySubCategory(SUB_CATEGORIES.STANDARD);
		setTimingMode(TIMING_MODES.INSTANT);
		// When a single campaign is selected, prefill the time field with its scheduled
		// time — but only if it's still in the future (a past time uses the timing mode).
		const singleTime = selectedCampaigns.length === 1 ? campaignsById[String(selectedCampaigns[0])]?.time : null;
		setSendTime(singleTime && moment(singleTime).isAfter(moment()) ? toDateTimeLocal(singleTime) : '');
		setNotes('');
		setTouched({});
		setShakeFields({});
		setReviewOpen(true);
	};

	const closeReview = () => {
		if (!isSubmitting) setReviewOpen(false);
	};

	// Earliest scheduled time among the selection that's already in the past (null if none).
	const pastScheduledTime = useMemo(() => {
		const pastTimes = selectedCampaigns
			.map(id => campaignsById[String(id)]?.time)
			.filter(t => !!t && moment(t).isBefore(moment()));
		if (!pastTimes.length) return null;
		return pastTimes.sort((a, b) => moment(a).valueOf() - moment(b).valueOf())[0];
	}, [selectedCampaigns, campaignsById]);
	const selectionHasPastTime = !!pastScheduledTime;
	// Show the Instant/Schedule timing picker only when approving a past-scheduled campaign.
	const showTimingMode = action === ACTIONS.APPROVE && selectionHasPastTime;
	// INSTANT locks the time field (auto now + delay); SCHEDULE lets the user pick.
	const timeLocked = showTimingMode && timingMode === TIMING_MODES.INSTANT;
	// The time field is editable ONLY in SCHEDULE mode. A future-scheduled campaign
	// (no timing picker) keeps its original time, so the field is disabled + not sent.
	const canEditTime = showTimingMode && timingMode === TIMING_MODES.SCHEDULE;
	const instantPreview = moment().add(INSTANT_DELAY_MINUTES, MINUTE_UNIT).format(DATETIME_LOCAL_FORMAT);

	// The time value that will actually be sent on approve:
	// - future scheduled (no picker) → '' (omit time, keep original schedule)
	// - INSTANT → now + delay
	// - SCHEDULE → the picked value
	const computeApproveTime = () => {
		if (!showTimingMode) return '';
		return timeLocked ? moment().add(INSTANT_DELAY_MINUTES, MINUTE_UNIT).format(DATETIME_LOCAL_FORMAT) : sendTime;
	};

	// Required-field errors (derived). Rendered as red helper text ONLY once the
	// field is touched (blurred) or a submit was attempted — no Swal, no default red.
	const subCategoryError =
		action === ACTIONS.APPROVE && selectionHasUtility && !utilitySubCategory
			? 'Please choose an internal sub category.'
			: '';
	const notesError = action === ACTIONS.REJECT && !notes.trim() ? 'Rejection notes are required.' : '';
	// In SCHEDULE mode a future date/time is required.
	let timeError = '';
	if (showTimingMode && timingMode === TIMING_MODES.SCHEDULE) {
		if (!sendTime) timeError = 'Please pick a date & time.';
		else if (moment(sendTime).isBefore(moment())) timeError = 'Please pick a future date & time.';
	}
	const isReviewInvalid = !!subCategoryError || !!notesError || !!timeError;

	const markTouched = field => setTouched(prev => ({ ...prev, [field]: true }));

	const handleReviewSubmit = () => {
		if (selectedCampaigns.length === 0) {
			// Not a text field — unreachable from the UI (button hides when nothing is selected).
			showValidationError('Please select at least one campaign.');
			return;
		}
		if (isReviewInvalid) {
			// Button stays enabled: reveal the inline errors + shake the invalid field(s).
			setTouched({ notes: true, subCategory: true, time: true });
			setShakeFields({ notes: !!notesError, subCategory: !!subCategoryError, time: !!timeError });
			return;
		}
		setConfirmOpen(true);
	};

	// Promise wrapper around the callback-based request helper.
	const requestAsync = args =>
		new Promise((resolve, reject) => {
			actionRequest({ ...args, onSuccess: resolve, onError: (_message, err) => reject(err) });
		});

	const handleConfirm = async () => {
		const ids = [...selectedCampaigns];
		setIsSubmitting(true);

		const calls = ids.map(id => {
			if (action === ACTIONS.APPROVE) {
				const payload = {
					campaign_id: Number(id),
					// UTILITY → the picked value; anything else → STANDARD.
					internal_sub_category: resolveSubCategory(campaignsById[String(id)], utilitySubCategory)
				};
				// Timing: instant → now + delay; schedule/normal → the field value (optional).
				const approveTime = computeApproveTime();
				if (approveTime) payload.time = approveTime;
				return requestAsync({
					module: CAMPAIGN_APPROVALS_MODULE,
					method: CAMPAIGN_APPROVALS_METHODS.approve,
					payload
				});
			}
			return requestAsync({
				module: CAMPAIGN_APPROVALS_MODULE,
				method: CAMPAIGN_APPROVALS_METHODS.reject,
				payload: { campaign_id: Number(id), notes: notes.trim() }
			});
		});

		const results = await Promise.allSettled(calls);
		const fulfilled = results.filter(r => r.status === 'fulfilled');
		const rejected = results.filter(r => r.status === 'rejected');
		const failed = rejected.length;
		const succeeded = fulfilled.length;
		const verb = action === ACTIONS.APPROVE ? 'approved' : 'rejected';

		setIsSubmitting(false);
		setConfirmOpen(false);
		setReviewOpen(false);
		setSelectedCampaigns([]);

		if (failed === 0) {
			// Single action → surface the API's own message; batch → frontend summary.
			if (succeeded === 1) {
				showApiSuccess(fulfilled[0].value, `Campaign ${verb} successfully.`);
			} else {
				showApiSuccess(null, `${succeeded} campaign(s) ${verb} successfully.`);
			}
		} else if (succeeded > 0) {
			// Partial: lead with counts, append the first API error message if present.
			const apiMsg = extractErrorMessage(rejected[0].reason);
			showWarning(`${succeeded} campaign(s) ${verb}, ${failed} failed.${apiMsg ? ` ${apiMsg}` : ''}`);
		} else {
			showApiError(rejected[0].reason, `Failed to ${action} the selected campaign(s).`);
		}

		setRefreshKey(prev => prev + 1);
	};

	// ---- Right-side action button (only when something is selected) ----
	const rightButtons =
		selectedCampaigns.length > 0 ? (
			<ButtonOmni
				onClick={openReview}
				variant="outlined"
				size="medium"
				label={
					<Box display="flex" alignItems="center">
						<FactCheckIcon style={BUTTON_ICON_STYLE} />
						Review Selected ({selectedCampaigns.length})
					</Box>
				}
			/>
		) : null;

	// ---- Review dialog content ----
	const reviewContent = (
		<div className={classes.formContent}>
			<Typography className={classes.summary}>
				You are about to review <strong>{selectedCampaigns.length}</strong> campaign(s) under{' '}
				<strong>{activeTab}</strong>.
			</Typography>
			<FormFieldOmni
				label="Action"
				variant="outlined"
				type="select"
				value={action}
				onChange={e => setAction(e.target.value)}
				options={ACTION_OPTIONS}
			/>
			{action === ACTIONS.APPROVE ? (
				<>
					{selectionHasUtility ? (
						<>
							<FormFieldOmni
								label="Internal Sub Category (UTILITY) *"
								variant="outlined"
								type="select"
								value={utilitySubCategory}
								onChange={e => setUtilitySubCategory(e.target.value)}
								onBlur={() => markTouched('subCategory')}
								options={UTILITY_SUB_CATEGORY_OPTIONS}
								error={touched.subCategory ? subCategoryError : ''}
								className={shakeFields.subCategory ? classes.shakeField : ''}
								onAnimationEnd={() => setShakeFields(prev => ({ ...prev, subCategory: false }))}
							/>
							<Typography className={classes.summary}>
								Applies to UTILITY campaigns. Non-UTILITY campaigns are approved as{' '}
								<strong>standard</strong>.
							</Typography>
						</>
					) : (
						<Typography className={classes.summary}>
							The selected campaign(s) will be approved as <strong>standard</strong>.
						</Typography>
					)}
					{showTimingMode && (
						<>
							<Typography className={classes.summary}>
								The scheduled time (
								<strong>{moment(pastScheduledTime).format(globalDateFormat)}</strong>) is in the past.
								Choose how to send it:
							</Typography>
							<FormFieldOmni
								label="Timing"
								variant="outlined"
								type="select"
								value={timingMode}
								onChange={e => {
									const mode = e.target.value;
									setTimingMode(mode);
									// Seed Schedule with a near-future default (now + delay); user can change it.
									if (mode === TIMING_MODES.SCHEDULE && !sendTime) {
										setSendTime(
											moment()
												.add(INSTANT_DELAY_MINUTES, MINUTE_UNIT)
												.format(DATETIME_LOCAL_FORMAT)
										);
									}
								}}
								options={TIMING_OPTIONS}
							/>
						</>
					)}
					<FormFieldOmni
						label={showTimingMode ? 'Send Time' : 'Scheduled Time'}
						variant="outlined"
						type="datetime-local"
						value={timeLocked ? instantPreview : sendTime}
						onChange={e => setSendTime(e.target.value)}
						onBlur={() => markTouched('time')}
						disabled={!canEditTime}
						InputLabelProps={{ shrink: true }}
						error={touched.time ? timeError : ''}
						className={shakeFields.time ? classes.shakeField : ''}
						onAnimationEnd={() => setShakeFields(prev => ({ ...prev, time: false }))}
					/>
					<Typography className={classes.summary}>
						{showTimingMode
							? timingMode === TIMING_MODES.INSTANT
								? `Will be sent about ${INSTANT_DELAY_MINUTES} minutes after approval.`
								: 'Pick a future date & time to send.'
							: 'The scheduled time is in the future — each campaign keeps its original time.'}
					</Typography>
				</>
			) : (
				<FormFieldOmni
					label="Rejection Notes *"
					variant="outlined"
					value={notes}
					onChange={e => setNotes(e.target.value)}
					onBlur={() => markTouched('notes')}
					multiline
					rows={NOTES_ROWS}
					placeholder="e.g. Wrong audience list, please recreate"
					error={touched.notes ? notesError : ''}
					className={shakeFields.notes ? classes.shakeField : ''}
					onAnimationEnd={() => setShakeFields(prev => ({ ...prev, notes: false }))}
				/>
			)}
		</div>
	);

	const reviewActions = (
		<div className={classes.formActions}>
			<ButtonOmni onClick={closeReview} variant="outlined" label="Cancel" disabled={isSubmitting} />
			<ButtonOmni
				onClick={handleReviewSubmit}
				variant="outlined"
				label={
					isSubmitting ? (
						<Box display="flex" alignItems="center" gap={1}>
							<CircularProgress size={INLINE_SPINNER_SIZE} color="inherit" />
							<span>Processing...</span>
						</Box>
					) : action === ACTIONS.APPROVE ? (
						'Approve'
					) : (
						'Reject'
					)
				}
				disabled={isSubmitting}
			/>
		</div>
	);

	const confirmVerb = action === ACTIONS.APPROVE ? 'approve' : 'reject';

	// Confirmation body: list each selected campaign's id + template name.
	// Kept as inline spans (message renders inside a <Typography> / <p>).
	const confirmMessage = (
		<>
			Are you sure you want to {confirmVerb} the following {selectedCampaigns.length} campaign(s)?
			<span style={{ display: 'block', marginTop: 10, maxHeight: CONFIRM_LIST_MAX_HEIGHT, overflowY: 'auto' }}>
				{selectedCampaigns.map(id => {
					const camp = campaignsById[String(id)];
					return (
						<span key={id} style={{ display: 'block', padding: '2px 0' }}>
							<strong>#{id}</strong> — {camp?.template_name || '-'}
						</span>
					);
				})}
			</span>
		</>
	);

	return (
		<div className={classes.content}>
			<PageHeading icon={FactCheckIcon} title="Campaign Approvals" />

			<GlobalTabs tabs={TABS} activeTab={activeTab} onChange={handleTabChange} />

			<DateFilterWithExport
				showDateRange={false}
				showExport={false}
				showRefresh
				onRefresh={handleRefresh}
				customRightContent={rightButtons}
			/>

			<TableOmniExpandable
				rows={filteredClients}
				headCells={outerHeadCells}
				innerHeadCells={innerHeadCells}
				getInnerRows={row => row.campaigns || []}
				outerRowKey="client_id"
				innerRowKey="id"
				checkBox
				selectedInner={selectedCampaigns}
				setSelectedInner={setSelectedCampaigns}
				loading={listLoading}
				fixedHeight
				maxTableHeight={TABLE_MAX_HEIGHT}
				perPageRows={TABLE_PER_PAGE}
				rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
				emptyMessage={`No pending campaigns for ${activeTab}`}
			/>

			{/* Review dialog — pick approve/reject + fill required field */}
			<ModalOmni
				open={reviewOpen}
				handleClose={closeReview}
				title="Review Campaigns"
				content={reviewContent}
				actions={reviewActions}
				customWidth={REVIEW_MODAL_WIDTH}
				disableBackdropClick={isSubmitting}
				disableEscapeKeyDown={isSubmitting}
			/>

			{/* Confirmation dialog for approve / reject */}
			<DeleteConfirmationDialog
				open={confirmOpen}
				onClose={() => !isSubmitting && setConfirmOpen(false)}
				onConfirm={handleConfirm}
				title={action === ACTIONS.APPROVE ? 'Approve Campaigns' : 'Reject Campaigns'}
				message={confirmMessage}
				confirmLabel={action === ACTIONS.APPROVE ? 'Approve' : 'Reject'}
				loading={isSubmitting}
			/>
		</div>
	);
}

export default CampaignApprovals;
